import '../styles/main.scss';
import { initHome } from './pages/home.js';
import { initFichaServicio } from './pages/ficha-servicio.js';
import { initEquipos } from './pages/equipos.js';
import { initMapaContacto } from './pages/contacto.js';
import { initFichaProyecto } from './pages/ficha-proyecto.js';

const initServicesAccordion = () => {
  const accordion = document.querySelector('.mobile-nav__services-accordion');
  const toggle = document.querySelector('.mobile-nav__services-toggle');
  if (!accordion || !toggle) return;

  const isMobile = () => window.innerWidth < 768;

  const close = () => {
    accordion.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
  };

  toggle.addEventListener('click', () => {
    if (!isMobile()) return;
    const isOpen = accordion.classList.contains('is-open');
    accordion.classList.toggle('is-open', !isOpen);
    toggle.setAttribute('aria-expanded', String(!isOpen));
  });

  return close;
};

const initHamburgerMenu = (resetAccordion) => {
  const hamburger = document.querySelector('.site-header__hamburger');
  const mobileNav = document.querySelector('.mobile-nav');
  const closeBtn = document.querySelector('.mobile-nav__close');
  const overlay = document.querySelector('.nav-overlay');
  if (!hamburger || !mobileNav) return;

  const open = () => {
    hamburger.setAttribute('aria-expanded', 'true');
    mobileNav.classList.add('is-open');
    mobileNav.setAttribute('aria-hidden', 'false');
    overlay?.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
  };

  const close = () => {
    hamburger.setAttribute('aria-expanded', 'false');
    mobileNav.classList.remove('is-open');
    mobileNav.setAttribute('aria-hidden', 'true');
    overlay?.classList.remove('is-visible');
    document.body.style.overflow = '';
    resetAccordion?.();
  };

  hamburger.addEventListener('click', open);
  closeBtn?.addEventListener('click', close);
  overlay?.addEventListener('click', close);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });
};

const initActiveNavLink = () => {
  const current = window.location.pathname.split('/').pop() || 'index.html';
  const allLinks = document.querySelectorAll('.mobile-nav__link');
  allLinks.forEach((link) => {
    if (link.getAttribute('href') === current) link.classList.add('is-active');
  });
};

const initLangDropdown = () => {
  const dropdown = document.querySelector('.site-header__lang-dropdown');
  const btn = dropdown?.querySelector('.site-header__lang');
  const menu = dropdown?.querySelector('.site-header__lang-menu');
  const options = dropdown?.querySelectorAll('.site-header__lang-option');
  const currentLabel = dropdown?.querySelector('.site-header__lang-current');
  if (!dropdown || !btn) return;

  const close = () => {
    dropdown.classList.remove('is-open');
    btn.setAttribute('aria-expanded', 'false');
  };

  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    const isOpen = dropdown.classList.contains('is-open');
    isOpen ? close() : (dropdown.classList.add('is-open'), btn.setAttribute('aria-expanded', 'true'));
  });

  options?.forEach((opt) => {
    opt.addEventListener('click', () => {
      options.forEach((o) => o.classList.remove('is-selected'));
      opt.classList.add('is-selected');
      if (currentLabel) currentLabel.textContent = opt.textContent.trim();
      close();
    });
  });

  document.addEventListener('click', close);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
};

const initScrollHeader = () => {
  const header = document.querySelector('.site-header');
  if (!header) return;
  const toggle = () => header.classList.toggle('is-scrolled', window.scrollY > 0);
  toggle();
  window.addEventListener('scroll', toggle, { passive: true });
};

const initReveal = () => {
  const items = document.querySelectorAll('.js-reveal');
  if (!items.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const siblings = entry.target.parentElement.querySelectorAll('.js-reveal');
        const index = Array.from(siblings).indexOf(entry.target);
        setTimeout(() => entry.target.classList.add('is-visible'), index * 100);
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.15 }
  );

  items.forEach((el) => observer.observe(el));
};

const initCounterAnimation = () => {
  const numbers = document.querySelectorAll(
    '.indicadores__number[data-count], .clientes__stat-number[data-count], .sobre__stat-number[data-count], .proyectos-intro__stat-number[data-count]'
  );
  if (!numbers.length) return;

  const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

  const animateCount = (el) => {
    const target = parseInt(el.dataset.count, 10);
    const prefix = el.dataset.prefix ?? '';
    const suffix = el.dataset.suffix ?? '';
    const duration = 1800;
    const start = performance.now();

    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const value = Math.round(easeOutQuart(progress) * target);
      el.textContent = prefix + value + suffix;
      if (progress < 1) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        animateCount(entry.target);
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.4 }
  );

  numbers.forEach((el) => observer.observe(el));
};

// Etiquetas para el mensaje de "campo obligatorio" — el form no usa <label>,
// solo placeholder + icono, así que no hay de dónde leer el nombre visible.
const FORM_FIELD_LABELS = {
  nombre: 'Nombre completo',
  telefono: 'Teléfono',
  correo: 'Correo electrónico',
  servicio: 'Servicio de interés',
  empresa: 'Empresa',
  mensaje: 'Mensaje',
};

// Mismas reglas que valida el servidor en gg_handle_contacto() — se corren acá
// antes para no pegarle a la red si el campo ya está mal.
const FORM_FIELD_VALIDATORS = {
  nombre: {
    test: (v) => /^[A-Za-zÁÉÍÓÚáéíóúÑñÜü\s]{3,}$/.test(v),
    message: 'El nombre debe tener al menos 3 letras y solo puede contener letras.',
  },
  telefono: {
    test: (v) => /^[0-9]{9}$/.test(v),
    message: 'El teléfono debe tener exactamente 9 dígitos numéricos.',
  },
  correo: {
    test: (v) => /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(v),
    message: 'Ingresa un correo electrónico válido (ej: nombre@dominio.com).',
  },
};

// Devuelve TODOS los mensajes de error encontrados (array vacío si todo está
// bien), no solo el primero. Los campos que el form no tiene (ej. "servicio"
// en el compacto de ficha-servicio) se saltan solos.
const validateContactForm = (form) => {
  const errors = [];

  for (const name of Object.keys(FORM_FIELD_LABELS)) {
    const field = form.elements[name];
    if (!field) continue;

    const value = field.value.trim();
    if (!value) {
      errors.push(`El campo "${FORM_FIELD_LABELS[name]}" es obligatorio.`);
      continue;
    }

    const rule = FORM_FIELD_VALIDATORS[name];
    if (rule && !rule.test(value)) {
      errors.push(rule.message);
    }
  }

  return errors;
};

// Envía los formularios por fetch en vez de hacer submit nativo: evita el
// recargo/scroll de página y muestra la confirmación en el mismo lugar donde
// hoy la renderiza PHP a partir de $_GET['contacto'] (ver formulario.php).
const initContactForms = () => {
  const forms = document.querySelectorAll('.formulario__form');
  if (!forms.length) return;

  const defaultMessages = {
    ok: 'Mensaje enviado correctamente. Nos pondremos en contacto pronto.',
    error: 'Por favor completa los campos requeridos (nombre y correo válido).',
  };

  forms.forEach((form) => {
    const submitBtn = form.querySelector('.formulario__submit');

    // messageOrMessages: un string, o un array cuando hay varios campos con
    // error — en ese caso se listan todos en vez de mostrar solo el primero.
    const showAlert = (type, messageOrMessages) => {
      const existing = form.previousElementSibling;
      if (existing?.classList.contains('formulario__alert')) existing.remove();

      const messages = Array.isArray(messageOrMessages) ? messageOrMessages : [messageOrMessages];

      const alert = document.createElement('div');
      alert.className = `formulario__alert formulario__alert--${type}`;

      if (type === 'ok' && window.ggFormCheckIconUrl) {
        const icon = document.createElement('img');
        icon.className = 'formulario__alert-icon';
        icon.src = window.ggFormCheckIconUrl;
        icon.alt = '';
        icon.setAttribute('aria-hidden', 'true');
        alert.appendChild(icon);
      }

      if (messages.length > 1) {
        const list = document.createElement('ul');
        list.className = 'formulario__alert-list';
        messages.forEach((msg) => {
          const item = document.createElement('li');
          item.className = 'formulario__alert-text';
          item.textContent = msg;
          list.appendChild(item);
        });
        alert.appendChild(list);
      } else {
        const text = document.createElement('p');
        text.className = 'formulario__alert-text';
        text.textContent = messages[0];
        alert.appendChild(text);
      }

      form.insertAdjacentElement('beforebegin', alert);
    };

    const sendForm = () => {
      const formData = new FormData(form);
      // getAttribute, no form.action: el campo oculto <input name="action"> que
      // exige admin-post.php tapa la propiedad .action del form con ese elemento.
      const actionUrl = form.getAttribute('action');

      fetch(actionUrl, {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            showAlert('ok', data.data?.message || defaultMessages.ok);
            form.reset();
          } else {
            const messages = data.data?.messages?.length
              ? data.data.messages
              : [data.data?.message || defaultMessages.error];
            showAlert('error', messages);
          }
        })
        .catch((err) => {
          console.error('[formulario] fallo al enviar:', err);
          showAlert('error', defaultMessages.error);
        })
        .finally(() => {
          if (submitBtn) submitBtn.disabled = false;
        });
    };

    form.addEventListener('submit', (e) => {
      e.preventDefault();

      const validationErrors = validateContactForm(form);
      if (validationErrors.length) {
        showAlert('error', validationErrors);
        return;
      }

      if (submitBtn) submitBtn.disabled = true;

      const siteKey = window.ggRecaptchaSiteKey;
      if (siteKey && typeof window.grecaptcha !== 'undefined') {
        window.grecaptcha.ready(() => {
          window.grecaptcha.execute(siteKey, { action: 'contacto' }).then((token) => {
            const input = form.querySelector('input[name="g-recaptcha-response"]');
            if (input) input.value = token;
            sendForm();
          });
        });
      } else {
        sendForm();
      }
    });
  });
};

document.addEventListener('DOMContentLoaded', () => {
  const resetAccordion = initServicesAccordion();
  initHamburgerMenu(resetAccordion);
  initActiveNavLink();
  initLangDropdown();
  initScrollHeader();
  initReveal();
  initCounterAnimation();
  initHome();
  initFichaServicio();
  initEquipos();
  initMapaContacto();
  initFichaProyecto();
  initContactForms();
});
